import type { DiffChange } from './diff-parser';

/**
 * Extract specific line ranges from raw file content
 * @param rawContent - The raw file content as a string
 * @param startLine - Starting line number (1-based)
 * @param endLine - Ending line number (1-based, inclusive)
 * @returns Array of line strings
 */
export function extractLinesFromRaw(rawContent: string, startLine: number, endLine: number): string[] {
  if (!rawContent) return [];

  // Handle both Unix (\n) and Windows (\r\n) line endings
  const lines = rawContent.split(/\r?\n/);
  // Convert to 0-based indexing and ensure we don't go out of bounds
  const start = Math.max(0, startLine - 1);
  const end = Math.min(lines.length, endLine);

  if (start >= end) return [];

  return lines.slice(start, end);
}

/**
 * Convert raw file lines to DiffChange objects for context display
 * @param rawLines - Array of raw line strings
 * @param startOldLine - Starting old line number
 * @param startNewLine - Starting new line number
 * @returns Array of DiffChange objects with type 'normal'
 */
export function createContextLines(rawLines: string[], startOldLine: number, startNewLine: number): DiffChange[] {
  return rawLines.map((line, index) => ({
    type: 'normal' as const,
    content: ` ${line}`, // Add leading space to match diff format
    oldLineNumber: startOldLine + index,
    newLineNumber: startNewLine + index,
  }));
}

/**
 * Calculate the line range for a gap between two hunks
 * @param prevHunk - The previous hunk
 * @param nextHunk - The next hunk
 * @returns Object with old and new line ranges for the gap
 */
export function calculateGapRange(
  prevHunk: { oldStart: number; oldLines: number; newStart: number; newLines: number },
  nextHunk: { oldStart: number; newStart: number }
): {
  startOldLine: number;
  endOldLine: number;
  startNewLine: number;
  endNewLine: number;
  count: number;
} {
  // Calculate where the previous hunk ends
  const prevEndOldLine = prevHunk.oldStart + prevHunk.oldLines - 1;
  const prevEndNewLine = prevHunk.newStart + prevHunk.newLines - 1;

  // Gap starts right after the previous hunk ends
  const startOldLine = prevEndOldLine + 1;
  const startNewLine = prevEndNewLine + 1;

  // Gap ends right before the next hunk starts
  const endOldLine = nextHunk.oldStart - 1;
  const endNewLine = nextHunk.newStart - 1;

  // Calculate gap size (use the larger of old/new gap)
  const oldGap = endOldLine - startOldLine + 1;
  const newGap = endNewLine - startNewLine + 1;
  const count = Math.max(oldGap, newGap);

  return {
    startOldLine,
    endOldLine,
    startNewLine,
    endNewLine,
    count,
  };
}

/**
 * Create a unique identifier for a gap
 * @param hunkIndex - Index of the hunk this gap appears before
 * @param startOldLine - Starting old line number of the gap
 * @param startNewLine - Starting new line number of the gap
 * @returns Unique gap identifier
 */
export function createGapId(hunkIndex: number, startOldLine: number, startNewLine: number): string {
  return `gap-${hunkIndex}-${startOldLine}-${startNewLine}`;
}

/**
 * Validate that a line range is reasonable to expand
 * @param count - Number of lines in the gap
 * @param maxLines - Maximum allowed lines (default: 1200)
 * @returns true if the gap can be expanded, false otherwise
 */
export function validateGapExpansion(count: number, maxLines: number = 1200): boolean {
  if (count <= 0) {
    return false;
  }

  if (count > maxLines) {
    return false;
  }

  return true;
}
